import type { EligibilityCheckRequestContent } from "@/generated/stedi/healthcare";
import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import { practitionerEnumerationType, type PractitionerRegistryData } from "@/src/lib/practitioner-registry-data";
import type { Coverage } from "@/src/models/coverage";
import { getPatientPrimaryCoverage, type Patient } from "@/src/models/patient";
import type { PersonGender } from "@/src/models/person";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";

export type EligibilityProviderAddress = {
  address1: string;
  city: string;
  state: string;
  postalCode?: string;
};

export type EligibilityCheckInputs = {
  customerPayer: string;
  subscriberGivenName: string;
  subscriberFamilyName: string;
  subscriberId: string;
  dateOfBirth: string;
  providerNpi: string;
  providerGivenName?: string;
  providerFamilyName?: string;
  providerOrganizationName?: string;
  serviceTypeCodes?: string[];
  providerAddress?: EligibilityProviderAddress;
  checkAsDependent?: boolean;
  patientGivenName?: string;
  patientFamilyName?: string;
  patientGender?: PersonGender;
  relationshipToSubscriber?: RelationshipToSubscriber;
  subscriberDateOfBirth?: string;
};

export type EligibilityCheckInputFields = {
  customerPayer?: string | null;
  subscriberGivenName?: string | null;
  subscriberFamilyName?: string | null;
  subscriberId?: string | null;
  dateOfBirth?: string | null;
  providerNpi?: string | null;
  providerGivenName?: string | null;
  providerFamilyName?: string | null;
  providerOrganizationName?: string | null;
  serviceTypeCodes?: string[] | null;
  providerAddress?: EligibilityProviderAddress | null;
  checkAsDependent?: boolean | null;
  patientGivenName?: string | null;
  patientFamilyName?: string | null;
  patientGender?: PersonGender | null;
  relationshipToSubscriber?: RelationshipToSubscriber | null;
  subscriberDateOfBirth?: string | null;
};

type EligibilityCoverageSource = Pick<
  Coverage,
  | "customerPayer"
  | "subscriberGivenName"
  | "subscriberFamilyName"
  | "subscriberId"
  | "subscriberDateOfBirth"
  | "relationshipToSubscriber"
>;

type EligibilityPatientSource = Pick<Patient, "givenName" | "familyName" | "dateOfBirth"> & {
  gender?: PersonGender | null;
};

type EligibilityFieldKey = keyof EligibilityCheckInputFields;

const ELIGIBILITY_FIELD_CHECKS: readonly {
  key: EligibilityFieldKey;
  label: string;
  requiredError: string;
}[] = [
  { key: "customerPayer", label: "Payor ID", requiredError: "Primary coverage customer payer is required" },
  { key: "subscriberGivenName", label: "Subscriber first name", requiredError: "Subscriber first name is required" },
  { key: "subscriberFamilyName", label: "Subscriber last name", requiredError: "Subscriber last name is required" },
  { key: "subscriberId", label: "Subscriber member ID", requiredError: "Subscriber member ID is required" },
  { key: "dateOfBirth", label: "Patient date of birth", requiredError: "Patient date of birth is required" },
  { key: "providerNpi", label: "Provider NPI", requiredError: "Provider NPI is required" },
];

const SUBSCRIBER_ELIGIBILITY_FIELD_CHECKS = ELIGIBILITY_FIELD_CHECKS.filter((field) => field.key !== "providerNpi");

type PractitionerEligibilityIdentity = {
  givenName?: string | null;
  familyName?: string | null;
  npiNumber?: string | null;
  npiRegistryEntry?: PractitionerRegistryData | null;
};

function isOrganizationEligibilityProvider(provider: PractitionerEligibilityIdentity): boolean {
  return practitionerEnumerationType(provider.npiRegistryEntry ?? null) === "NPI-2";
}

export function providerEligibilityFieldsFromPractitioner(
  provider: PractitionerEligibilityIdentity | null | undefined
): Pick<
  EligibilityCheckInputFields,
  "providerNpi" | "providerGivenName" | "providerFamilyName" | "providerOrganizationName"
> {
  if (!provider) {
    return {};
  }

  if (isOrganizationEligibilityProvider(provider)) {
    return {
      providerNpi: provider.npiNumber,
      providerOrganizationName: provider.givenName,
    };
  }

  return {
    providerNpi: provider.npiNumber,
    providerGivenName: provider.givenName,
    providerFamilyName: provider.familyName,
  };
}

export function hasValidProviderIdentity(fields: EligibilityCheckInputFields): boolean {
  return !!fields.providerOrganizationName || (!!fields.providerGivenName && !!fields.providerFamilyName);
}

function appendMissingProviderFieldLabels(fields: EligibilityCheckInputFields, missing: string[]): void {
  if (hasValidProviderIdentity(fields)) {
    return;
  }

  if (!fields.providerOrganizationName) {
    if (!fields.providerGivenName) {
      missing.push("Provider first name");
    }

    if (!fields.providerFamilyName) {
      missing.push("Provider last name");
    }
  }
}

export function getMissingSubscriberEligibilityCheckFieldLabels(fields: EligibilityCheckInputFields): string[] {
  return SUBSCRIBER_ELIGIBILITY_FIELD_CHECKS.filter((field) => !fields[field.key]).map((field) => field.label);
}

export function getMissingEligibilityCheckFieldLabels(fields: EligibilityCheckInputFields): string[] {
  const missing = ELIGIBILITY_FIELD_CHECKS.filter((field) => !fields[field.key]).map((field) => field.label);

  appendMissingProviderFieldLabels(fields, missing);

  return missing;
}

export function requireEligibilityCheckInputs(fields: EligibilityCheckInputFields): EligibilityCheckInputs {
  const missing = ELIGIBILITY_FIELD_CHECKS.find((field) => !fields[field.key]);

  if (missing) {
    throw new Error(missing.requiredError);
  }

  if (!hasValidProviderIdentity(fields)) {
    throw new Error("Provider organization name or provider first/last name is required");
  }

  return {
    customerPayer: fields.customerPayer!,
    subscriberGivenName: fields.subscriberGivenName!,
    subscriberFamilyName: fields.subscriberFamilyName!,
    subscriberId: fields.subscriberId!,
    dateOfBirth: fields.dateOfBirth!,
    providerNpi: fields.providerNpi!,
    providerGivenName: fields.providerGivenName ?? undefined,
    providerFamilyName: fields.providerFamilyName ?? undefined,
    providerOrganizationName: fields.providerOrganizationName ?? undefined,
    serviceTypeCodes: fields.serviceTypeCodes ?? undefined,
    providerAddress: fields.providerAddress ?? undefined,
    checkAsDependent: fields.checkAsDependent ?? undefined,
    patientGivenName: fields.patientGivenName ?? undefined,
    patientFamilyName: fields.patientFamilyName ?? undefined,
    patientGender: fields.patientGender ?? undefined,
    relationshipToSubscriber: fields.relationshipToSubscriber ?? undefined,
    subscriberDateOfBirth: fields.subscriberDateOfBirth ?? undefined,
  };
}

function eligibilityProvidersMatch(
  existingProvider: EligibilityCheckRequestContent["provider"],
  nextProvider: EligibilityCheckRequestContent["provider"]
): boolean {
  if (!existingProvider || !nextProvider) {
    return existingProvider === nextProvider;
  }

  if (existingProvider.npi !== nextProvider.npi) {
    return false;
  }

  if (existingProvider.organizationName || nextProvider.organizationName) {
    return existingProvider.organizationName === nextProvider.organizationName;
  }

  return existingProvider.firstName === nextProvider.firstName && existingProvider.lastName === nextProvider.lastName;
}

export function eligibilitySubscriberFieldsFromPatient(patient: Patient): EligibilityCheckInputFields {
  return eligibilitySubscriberFieldsFromCoverage(getPatientPrimaryCoverage(patient), patient);
}

function memberIdFromEligibilityCheckRequest(requestJson: unknown): string | null {
  if (!requestJson || typeof requestJson !== "object" || "skipped" in requestJson) {
    return null;
  }

  const memberId = (requestJson as EligibilityCheckRequestContent).subscriber?.memberId;

  return memberId?.trim() ? memberId.trim() : null;
}

export function resolveEligibilityMemberId(
  patient: Patient | null | undefined,
  eligibilityCheck: StediEligibilityCheck | null
): string | null {
  const fromCoverage = patient ? getPatientPrimaryCoverage(patient)?.subscriberId?.trim() : null;

  if (fromCoverage) {
    return fromCoverage;
  }

  return eligibilityCheck ? memberIdFromEligibilityCheckRequest(eligibilityCheck.requestJson) : null;
}

export function eligibilitySubscriberFieldsFromCoverage(
  coverage: EligibilityCoverageSource | null | undefined,
  patient: EligibilityPatientSource
): EligibilityCheckInputFields {
  const subscriberGivenName = coverage?.subscriberGivenName ?? patient.givenName;
  const subscriberFamilyName = coverage?.subscriberFamilyName ?? patient.familyName;
  const fields: EligibilityCheckInputFields = {
    customerPayer: coverage?.customerPayer,
    subscriberGivenName,
    subscriberFamilyName,
    subscriberId: coverage?.subscriberId,
    dateOfBirth: patient.dateOfBirth,
  };

  if (!coverage) {
    return fields;
  }

  const treatAsSelf =
    coverage.relationshipToSubscriber == null || coverage.relationshipToSubscriber === RelationshipToSubscriber.SELF;
  const namesMatch =
    subscriberGivenName?.toLowerCase().trim() === patient.givenName?.toLowerCase().trim() &&
    subscriberFamilyName?.toLowerCase().trim() === patient.familyName?.toLowerCase().trim();

  if (treatAsSelf && namesMatch) {
    return fields;
  }

  return {
    ...fields,
    checkAsDependent: true,
    patientGivenName: patient.givenName,
    patientFamilyName: patient.familyName,
    patientGender: patient.gender,
    relationshipToSubscriber: coverage.relationshipToSubscriber,
    subscriberDateOfBirth: coverage.subscriberDateOfBirth,
  };
}

export function eligibilityRequestMemberId(primaryPayerId: string, subscriberId: string): string {
  return primaryPayerId === "OSCAR" ? subscriberId.replace(/-/g, "") : subscriberId;
}

export function eligibilityRequestsMatch(
  existingRequest: unknown,
  nextRequest: EligibilityCheckRequestContent
): boolean {
  if (!existingRequest || typeof existingRequest !== "object") {
    return false;
  }

  const request = existingRequest as EligibilityCheckRequestContent;
  const existingStcs = request.encounter?.serviceTypeCodes ?? [];
  const nextStcs = nextRequest.encounter?.serviceTypeCodes ?? [];
  const existingAddress = request.provider?.address;
  const nextAddress = nextRequest.provider?.address;
  const existingDependent = request.dependents?.[0];
  const nextDependent = nextRequest.dependents?.[0];

  return (
    request.tradingPartnerServiceId === nextRequest.tradingPartnerServiceId &&
    request.subscriber?.memberId === nextRequest.subscriber?.memberId &&
    request.subscriber?.firstName === nextRequest.subscriber?.firstName &&
    request.subscriber?.lastName === nextRequest.subscriber?.lastName &&
    request.subscriber?.dateOfBirth === nextRequest.subscriber?.dateOfBirth &&
    eligibilityProvidersMatch(request.provider, nextRequest.provider) &&
    existingStcs.length === nextStcs.length &&
    existingStcs.every((code, index) => code === nextStcs[index]) &&
    (existingAddress?.address1 ?? null) === (nextAddress?.address1 ?? null) &&
    (existingAddress?.city ?? null) === (nextAddress?.city ?? null) &&
    (existingAddress?.state ?? null) === (nextAddress?.state ?? null) &&
    (existingAddress?.postalCode ?? null) === (nextAddress?.postalCode ?? null) &&
    (existingDependent?.firstName ?? null) === (nextDependent?.firstName ?? null) &&
    (existingDependent?.lastName ?? null) === (nextDependent?.lastName ?? null) &&
    (existingDependent?.dateOfBirth ?? null) === (nextDependent?.dateOfBirth ?? null) &&
    (existingDependent?.gender ?? null) === (nextDependent?.gender ?? null) &&
    (existingDependent?.individualRelationshipCode ?? null) === (nextDependent?.individualRelationshipCode ?? null)
  );
}
